import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Sync the active tab/section with a URL search param, making it shareable,
 * bookmarkable and refresh-stable. The URL is the single source of truth, so
 * browser Back/Forward navigates between sections with no extra wiring.
 *
 * - An invalid or missing param resolves to `defaultId` (never a blank screen).
 * - The default tab is not written to the URL, keeping default links clean.
 *
 * @param {string[]} validIds - allowed tab ids
 * @param {string} defaultId - tab id used when the param is missing/invalid
 * @param {string} [paramName='tab'] - search-param name to read/write
 * @returns {[string, (id: string) => void]} [activeTab, setActiveTab]
 */
const useTabParam = (validIds, defaultId, paramName = 'tab') => {
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get(paramName);
  const activeTab = validIds.includes(raw) ? raw : defaultId;

  const setActiveTab = useCallback(
    (id) => {
      const next = new URLSearchParams(searchParams);
      if (id === defaultId) {
        next.delete(paramName);
      } else {
        next.set(paramName, id);
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams, defaultId, paramName]
  );

  return [activeTab, setActiveTab];
};

export default useTabParam;
